import api from './api';

export interface ComissaoMembro {
  id: string;
  comissaoId: string;
  usuarioId: number;
  nomeCompleto?: string;
  email?: string;
  cargoFuncao?: string;
  perfil?: string;
  possuiPareceres: boolean;
  ativo: boolean;
  podeInativar: boolean;
  motivoBloqueioInativacao?: string;
  inativadoEm?: string;
  createdAt?: string;
  isPresidente: boolean;
  substituiMembroId?: string;
}

export interface Comissao {
  id: string;
  numero: number;
  numeroPortaria: string;
  anoPortaria: number;
  ativa: boolean;
  membros: ComissaoMembro[];
  createdAt?: string;
  updatedAt?: string;
}

export interface ComissaoParecer {
  id: string;
  planoTrabalhoId: string;
  comissaoMembroId: string;
  usuarioId?: number;
  resultado: 'APROVADO' | 'REPROVADO';
  justificativa: string;
  numeroComissaoSnapshot: number;
  numeroPortariaSnapshot: string;
  anoPortariaSnapshot: number;
  usuarioNomeSnapshot?: string;
  invalidado: boolean;
  createdAt?: string;
}

export interface ComissaoParecerMembroStatus {
  membroId: string;
  usuarioId?: number;
  nomeCompleto?: string;
  email?: string;
  cargoFuncao?: string;
  perfil?: string;
  statusParecer: 'APROVADO' | 'REPROVADO' | 'PENDENTE' | string;
  resultado?: 'APROVADO' | 'REPROVADO';
  dataEmissao?: string;
  justificativa?: string;
  numeroDaComissao?: number;
  numeroPortaria?: string;
  anoPortaria?: number;
  membroLogado: boolean;
  isPresidente: boolean;
}

export interface ComissaoParecerResumo {
  planoTrabalhoId: string;
  comissaoId?: string;
  numeroDaComissao?: number;
  numeroPortaria?: string;
  anoPortaria?: number;
  statusGeral: string;
  pareceresConcluidos: number;
  totalMembros: number;
  ataAnexada: boolean;
  tipoAta: string;
  membroLogado?: ComissaoMembro;
  membros: ComissaoParecerMembroStatus[];
  pareceres: ComissaoParecer[];
}

const comissaoService = {
  listar: async (): Promise<Comissao[]> => {
    const res = await api.get('/comissoes');
    return res.data;
  },

  buscar: async (id: string): Promise<Comissao> => {
    const res = await api.get(`/comissoes/${id}`);
    return res.data;
  },

  criar: async (dto: Partial<Comissao>): Promise<Comissao> => {
    const res = await api.post('/comissoes', dto);
    return res.data;
  },

  atualizar: async (id: string, dto: Partial<Comissao>): Promise<Comissao> => {
    const res = await api.put(`/comissoes/${id}`, dto);
    return res.data;
  },

  adicionarMembro: async (comissaoId: string, usuarioId: number): Promise<Comissao> => {
    const res = await api.post(`/comissoes/${comissaoId}/membros`, { usuarioId });
    return res.data;
  },

  inativarMembro: async (comissaoId: string, membroId: string): Promise<Comissao> => {
    const res = await api.delete(`/comissoes/${comissaoId}/membros/${membroId}`);
    return res.data;
  },

  substituirMembro: async (comissaoId: string, membroTitularId: string, usuarioId: number): Promise<Comissao> => {
    const res = await api.post(`/comissoes/${comissaoId}/membros/${membroTitularId}/substituir`, { usuarioId });
    return res.data;
  },

  designarPresidente: async (comissaoId: string, membroId: string): Promise<ComissaoMembro[]> => {
    const res = await api.put(`/comissoes/${comissaoId}/membros/${membroId}/presidente`);
    return res.data;
  },

  removerPresidente: async (comissaoId: string): Promise<ComissaoMembro[]> => {
    const res = await api.delete(`/comissoes/${comissaoId}/presidente`);
    return res.data;
  },

  uploadAtaPresidente: async (planoId: string, file: File): Promise<{ id: string; nomeOriginal: string; dataUpload?: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post(`/plano-trabalho/${planoId}/comissao/ata`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },

  emitirParecer: async (
    planoId: string,
    payload: { resultado: 'APROVADO' | 'REPROVADO'; justificativa: string }
  ): Promise<ComissaoParecer> => {
    const res = await api.post(`/planos-de-trabalho/${planoId}/pareceres`, payload);
    return res.data;
  },

  listarPareceresDoPlano: async (planoId: string): Promise<ComissaoParecer[]> => {
    const res = await api.get(`/planos-de-trabalho/${planoId}/pareceres`);
    return res.data;
  },

  resumoPareceresDoPlano: async (planoId: string): Promise<ComissaoParecerResumo> => {
    const res = await api.get(`/planos-de-trabalho/${planoId}/pareceres/resumo`);
    return res.data;
  },
};

export default comissaoService;
